/**
 * useDviSignals - Hook for fetching DVI signal intelligence data
 * 
 * Phase 5B: Signal Intelligence
 * Provides shop-specific approval rates, overcall rates, and quality scores.
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface ConditionMetrics {
  total_findings: number;
  avg_confidence: number;
  customer_approval_rate: number;
  advisor_approval_rate: number;
  applied_rate: number;
  overcall_rate: number;
}

interface CatalogJobMetrics {
  total_findings: number;
  customer_approval_rate: number;
  avg_confidence: number;
  condition_distribution: { green: number; yellow: number; red: number };
}

export interface DviSignalData {
  by_condition: Record<string, ConditionMetrics>;
  by_catalog_job: Record<string, CatalogJobMetrics>;
  quality_score: number | null;
  sample_count: number;
}

/**
 * Fetches DVI signal intelligence for a shop
 * 
 * @param shopId - Shop ID to fetch signals for
 * @param lookbackDays - Number of days to look back (default: 90)
 * @returns Signal data with approval rates, quality scores, etc.
 */
export function useDviSignals(shopId: string | null, lookbackDays: number = 90) {
  return useQuery({
    queryKey: ['dvi-signals', shopId, lookbackDays],
    queryFn: async (): Promise<DviSignalData | null> => {
      if (!shopId) return null;
      
      const { data, error } = await supabase.functions.invoke('dvi-compute-shop-signals', {
        body: { shop_id: shopId, lookback_days: lookbackDays },
      });
      
      if (error) throw error;
      return data as DviSignalData;
    },
    enabled: !!shopId,
    staleTime: 1000 * 60 * 30, // 30 minutes - signals don't change rapidly
    gcTime: 1000 * 60 * 60, // 1 hour cache
  });
}

/**
 * Gets signal-based UI hints for a finding group
 */
export function getSignalHints(
  signals: DviSignalData | null | undefined,
  condition: 'green' | 'yellow' | 'red' | null,
  confidence: number | null,
  primaryCatalogJobId: string | null
): {
  showHistoricallyDeclined: boolean;
  historicalApprovalRate: number | null;
  suggestMonitor: boolean;
  confidenceVsApprovalDelta: number | null;
} {
  const hints = {
    showHistoricallyDeclined: false,
    historicalApprovalRate: null as number | null,
    suggestMonitor: false,
    confidenceVsApprovalDelta: null as number | null,
  };

  if (!signals || !condition) return hints;

  // Check job-specific approval rate
  if (primaryCatalogJobId && signals.by_catalog_job[primaryCatalogJobId]) {
    const jobMetrics = signals.by_catalog_job[primaryCatalogJobId];
    if (jobMetrics.total_findings >= 3) {
      hints.historicalApprovalRate = jobMetrics.customer_approval_rate;
      
      // Show "Historically Declined" if approval rate < 40%
      if (jobMetrics.customer_approval_rate < 0.4) {
        hints.showHistoricallyDeclined = true;
      }
      
      // Calculate confidence vs approval delta
      if (confidence !== null) {
        hints.confidenceVsApprovalDelta = confidence - jobMetrics.customer_approval_rate;
      }
    }
  }

  // Check condition-specific patterns
  if (signals.by_condition[condition]) {
    const conditionMetrics = signals.by_condition[condition];
    
    // Suggest Monitor for low confidence + low approval conditions
    if (
      condition === 'red' && 
      confidence !== null && 
      confidence < 0.6 && 
      conditionMetrics.customer_approval_rate < 0.5
    ) {
      hints.suggestMonitor = true;
    }
  }

  return hints;
}

/**
 * Calculates a display-friendly quality level
 */
export function getQualityLevel(score: number | null): {
  level: 'insufficient' | 'low' | 'medium' | 'high' | 'excellent';
  label: string;
  color: string;
} {
  if (score === null) {
    return { level: 'insufficient', label: 'Insufficient Data', color: 'text-muted-foreground' };
  }
  if (score >= 80) {
    return { level: 'excellent', label: 'Excellent', color: 'text-green-600' };
  }
  if (score >= 65) {
    return { level: 'high', label: 'Good', color: 'text-green-500' };
  }
  if (score >= 50) {
    return { level: 'medium', label: 'Average', color: 'text-yellow-600' };
  }
  return { level: 'low', label: 'Needs Improvement', color: 'text-orange-600' };
}
